import { useEffect } from 'react'

import { ConsumerTag, PreferredMarketingNotification, AssetId } from '../model'
import { useMerchantCallback } from './merchant'
import { useTransactionMessage } from './transactionMessage'
import { usePersonalization } from './personalization'
import { BASE_PATH } from '../env'
import { useGetMarketingPreferencesQuery, useExperimentTrigger, usePostCheckoutExperiment, usePostCheckoutFlow } from '../state'

export const getCtaTagName = (assetId: string): string => `checkout.asset.${assetId}.cta`
export const getImpressionsTagName = (assetId: string): string => `checkout.asset.${assetId}.impressions`

export const getAssetImpressions = (assetId: string, tags: ConsumerTag): number => {
  const impressionsTag = tags[getImpressionsTagName(assetId)]

  if (impressionsTag === undefined || impressionsTag === null || impressionsTag === 'false') {
    return 0
  }

  return parseInt(impressionsTag, 10)
}

export const useSignalCheckoutCompletion: () => () => Promise<void> = () => {
  const { endTransaction } = useMerchantCallback()
  const endTransactionMessage = useTransactionMessage()

  return async () => {
    const enrichedEndTransactionMessage = {
      ...endTransactionMessage,
      keepCheckoutWindow: true,
    }
    await endTransaction(enrichedEndTransactionMessage)
  }
}

export const useGetLocaleWithFallback = (localeFile: string = 'postcheckout'): ((localeKey: string) => string[]) => {
  const flow = usePostCheckoutFlow()
  const {
    postCheckout: {
      asset: { id },
    },
  } = usePersonalization()
  const { value: variant } = usePostCheckoutExperiment()
  const ASSET_LOCALE = `${localeFile}:asset:${id}`

  return (localeKey: string) => [`${ASSET_LOCALE}.${localeKey}__${variant}-${flow}`, `${ASSET_LOCALE}.${localeKey}__${variant}`, `${ASSET_LOCALE}.${localeKey}`]
}

export const useGetAssetHeroSrc = (hasVariantImages = false): string => {
  const {
    postCheckout: {
      asset: { id },
    },
  } = usePersonalization()

  const { value: variant } = usePostCheckoutExperiment()

  const image = hasVariantImages ? `hero${variant}.jpg` : `hero.jpg`

  return [BASE_PATH, 'images/asset', id, image].join('/')
}

export const useSurveyUIConfig = (): {
  showImage: boolean
} => {
  const {
    postCheckout: {
      asset: { id },
    },
  } = usePersonalization()
  const showImageAssetIds = [AssetId.GoogleSSO]
  return { showImage: showImageAssetIds.includes(id) }
}

export const usePreferredMarketingNotification = (): PreferredMarketingNotification => {
  const { data: marketingPreferences } = useGetMarketingPreferencesQuery()

  return marketingPreferences?.pushNotification !== 'SUBSCRIBED' ? PreferredMarketingNotification.EMAIL : PreferredMarketingNotification.PUSH
}

export const usePostCheckoutTriggerExperiment = (): void => {
  const { postCheckout } = usePersonalization()
  const { experiment: postCheckoutExperiment } = usePostCheckoutExperiment()
  const postCheckoutExperimentTrigger = useExperimentTrigger(postCheckoutExperiment)

  useEffect(() => {
    const analyticsPayload = {
      eligibility: postCheckout.eligibility,
      asset: {
        id: postCheckout.asset.id,
        label: postCheckout.asset.label,
      },
      captureType: postCheckout.flow,
    }
    postCheckoutExperimentTrigger(analyticsPayload)
  }, [])
}
